import type {TokenProps} from '@primer/react'
import {Token} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {clsx} from 'clsx'
import {useEffect, useRef, useState} from 'react'

import styles from './IssueFieldSingleSelectValueToken.module.css'

// TODO issue_fields rename the @github-ui/issue-type-token to @github-ui/issue-tokens and move that to this package
export type IssueFieldSingleSelectValueTokenProps = {
  name: string
  color: string
  getTooltipText: (isTextTruncated: boolean) => string | undefined
  tokenClassName?: string
  containerClassName?: string
} & Pick<TokenProps, 'size'>

type IssueFieldMultiSelectValueOption = {id?: string; name: string; color: string}

export type IssueFieldMultiSelectValueTokensProps = {
  options: readonly IssueFieldMultiSelectValueOption[]
  getTooltipText: (isTextTruncated: boolean) => string | undefined
  tokenClassName?: string
  containerClassName?: string
} & Pick<TokenProps, 'size'>

export const IssueFieldSingleSelectValueToken = ({
  name,
  color,
  getTooltipText,
  size,
  tokenClassName,
  containerClassName,
}: IssueFieldSingleSelectValueTokenProps) => {
  const tokenRef = useRef<HTMLElement>(null)
  const [truncated, setTruncated] = useState(false)

  useEffect(() => setTruncated(isTextTruncated(tokenRef.current)), [setTruncated, tokenRef])

  const tooltipText = getTooltipText(truncated)

  const internal = (
    <Token
      className={clsx(styles.token, tokenClassName)}
      data-color={color}
      ref={tokenRef}
      // the span in the text is allowing us to check if the text is truncated
      text={<span className={styles.tokenText}>{name}</span>}
      as={tooltipText ? 'button' : 'span'}
      {...(tooltipText && {
        type: 'button',
        'aria-label': `${name} token`,
      })}
    />
  )

  return (
    <div className={clsx(styles.container, containerClassName)} data-size={size}>
      {tooltipText ? <Tooltip text={tooltipText}>{internal}</Tooltip> : internal}
    </div>
  )
}

export const IssueFieldMultiSelectValueTokens = ({
  options,
  getTooltipText,
  size,
  tokenClassName,
  containerClassName,
}: IssueFieldMultiSelectValueTokensProps) => {
  return (
    <div className={clsx(styles.multiContainer, containerClassName)} data-size={size}>
      {options.map((option, index) => (
        <IssueFieldSingleSelectValueToken
          key={option.id ?? `${option.name}-${index}`}
          name={option.name}
          color={option.color}
          getTooltipText={getTooltipText}
          size={size}
          tokenClassName={tokenClassName}
        />
      ))}
    </div>
  )
}
// This is not bullet proof, since the true inner width of the element is rounded down.
// I think it's pretty good though. See discussion here https://stackoverflow.com/questions/64689074/how-to-check-if-text-is-truncated-by-css-using-javascript
const isTextTruncated = (e: HTMLElement | null): boolean => {
  if (!e || !e.firstElementChild) return false
  return e.firstElementChild.scrollWidth > e.firstElementChild.clientWidth
}
